import { useCallback, useEffect, useMemo, type ReactElement } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'

import { trackEvent } from 'analytics/analytics'
import { promotionClickEvent, promotionViewEvent } from 'analytics/snowplow/events'
import LayoutContainer from 'components/Common/LayoutContainer'
import Image from 'components/Common/Image'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Places from 'constants/places'
import { useAppSelector } from 'hooks/reduxHooks'
import { getExperienceSearchPath } from 'lib/experiences/experienceUtils'
import TextButton from 'LuxKit/Button/TextButton'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'

const FATHERS_DAY_PROMOTION_CONTEXT = {
  promotionName: 'experiences_fathers_day',
  promotionType: 'banner',
} as const
const FATHERS_DAY_CLICK_ACTION = 'cta'

const BANNER_ARTWORK = [
  { id: 'desktop', image: { id: '2bgem3efyk2n71cm1nh6' }, width: 1140, height: 186, min: 'desktop' as const },
  { id: 'tablet', image: { id: '5ls1boq4n9l14crjg6p' }, width: 600, height: 140, min: 'tablet' as const, max: 'tablet' as const },
  { id: 'mobile', image: { id: 'k7rdr0ef9whv1jyzckvf' }, width: 375, height: 105, max: 'mobile' as const },
]

const CTA_BREAKPOINTS = [
  { size: 'small' as const, max: 'mobile' as const },
  { size: 'medium' as const, min: 'tablet' as const, max: 'tablet' as const },
  { size: 'large' as const, min: 'desktop' as const },
]

const BannerFrame = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.S};
`

const BannerImage = styled(Image)`
  display: block;
  width: 100%;
  height: auto;
`

const Cta = styled.div`
  position: absolute;
  z-index: 2;
  inset-inline-end: ${rem(16)};
  bottom: ${rem(16)};

  ${mediaQueryUp.desktop} {
    inset-inline-end: ${rem(24)};
    bottom: ${rem(24)};
  }
`

interface BannerCtaProps {
  size: 'small' | 'medium' | 'large'
  min?: App.ScreenSize
  max?: App.ScreenSize
  to: string
  label: string
  endIcon: ReactElement
  onClick: () => void
}

function BannerCta({ size, min, max, to, label, endIcon, onClick }: BannerCtaProps) {
  return (
    <CSSBreakpoint min={min} max={max}>
      <TextButton
        kind="primary"
        variant="ghost"
        size={size}
        shape="square"
        to={to}
        endIcon={endIcon}
        onClick={onClick}
      >
        {label}
      </TextButton>
    </CSSBreakpoint>
  )
}

function ExperiencesFathersDayBanner() {
  const intl = useIntl()
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const searchPath = useMemo(
    () => getExperienceSearchPath(Places.Australia, { featured: ['is-gift'] }),
    [],
  )
  const ctaEndIcon = useMemo(() => <LineAngleRightIcon />, [])
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5,
  })

  useEffect(() => {
    if (!inView) {
      return
    }

    trackEvent(promotionViewEvent({
      promotionContext: FATHERS_DAY_PROMOTION_CONTEXT,
    }))
  }, [inView])

  const handleCtaClick = useCallback(() => {
    trackEvent(promotionClickEvent({
      promotionContext: FATHERS_DAY_PROMOTION_CONTEXT,
      clickAction: FATHERS_DAY_CLICK_ACTION,
    }))
  }, [])

  const ctaLabel = intl.formatMessage({
    id: '26L5r-6DH5EvK3FiThYM',
    defaultMessage: 'View Father’s Day gifts',
    description: 'Call to action on the Father’s Day experiences homepage campaign banner linking to gift inspiration search',
  })
  const altText = intl.formatMessage({
    id: 'xKWDQuJZdXyHK40QN_cx',
    defaultMessage: 'Father’s Day experiences: ties are temporary, memories last a lifetime',
    description: 'Alt text for the Father’s Day experiences homepage campaign banner artwork without the call to action',
  })

  if (regionCode !== 'AU') {
    return null
  }

  return (
    <LayoutContainer>
      <BannerFrame ref={inViewRef} data-testid="experiences-fathers-day-banner">
        {BANNER_ARTWORK.map((artwork) => (
          <CSSBreakpoint key={artwork.id} min={artwork.min} max={artwork.max}>
            <BannerImage
              image={artwork.image}
              format="png"
              width={artwork.width}
              height={artwork.height}
              alt={altText}
            />
          </CSSBreakpoint>
        ))}
        <Cta>
          {CTA_BREAKPOINTS.map((breakpoint) => (
            <BannerCta
              key={breakpoint.size}
              size={breakpoint.size}
              min={breakpoint.min}
              max={breakpoint.max}
              to={searchPath}
              label={ctaLabel}
              endIcon={ctaEndIcon}
              onClick={handleCtaClick}
            />
          ))}
        </Cta>
      </BannerFrame>
    </LayoutContainer>
  )
}

export default ExperiencesFathersDayBanner
